import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users, Shield, ChevronDown, ChevronUp, Search,
  ArrowLeft, AlertCircle, Pause, Play, XCircle,
  RefreshCw, LinkIcon, UserPlus, Mail, Trash2,
} from 'lucide-react';
import {
  callAdminListUsers, callAdminSetUserRole, callAdminManageSubscription, callAdminGrantEntitlement,
  callAdminInviteClient, callAdminDeleteUser,
} from '../../services/admin';
import { callGetPlanCatalog, readCachedPlanCatalog, PlanCatalog } from '../../services/functions';

type UserData = {
  uid: string;
  email: string;
  displayName: string | null;
  role: string;
  activeProducts: string[];
  entitlements: Record<string, { plan: string; status: string; trialEndsAt?: any; payfast?: { token?: string; subscriptionStatus?: string } }>;
  createdAt: any;
};

type ProductId = 'vetaxis' | 'staffaxis' | 'noteaxis' | 'suite';
type EntitlementStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'none';

const PRODUCT_OPTIONS: Array<{ id: ProductId; name: string }> = [
  { id: 'vetaxis', name: 'VetAxis' },
  { id: 'staffaxis', name: 'StaffAxis' },
  { id: 'noteaxis', name: 'NoteAxis' },
  { id: 'suite', name: 'Suite Bundle' },
];

const DEFAULT_PLAN_BY_PRODUCT: Record<ProductId, string> = {
  vetaxis: 'starter',
  staffaxis: 'starter',
  noteaxis: 'standard',
  suite: 'suite_basic',
};

// trialEndsAt arrives as a serialized Firestore Timestamp ({_seconds}) over the
// callable wire, but tolerate real Timestamps and ISO strings too.
const trialEndsLabel = (v: any): string | null => {
  if (!v) return null;
  const d = typeof v.toDate === 'function' ? v.toDate()
    : v._seconds ? new Date(v._seconds * 1000)
    : new Date(v);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

type PlanOption = { id: string; name: string };

const planOptionsFor = (catalog: PlanCatalog | null, productId: ProductId): PlanOption[] => {
  // The catalog types each product's plan list separately; normalize to the
  // common fields we need so array methods work across the union.
  const plans: Array<{ id: string; name: string; order?: number; active?: boolean }> =
    (catalog?.[productId] as any) || [];
  return plans
    .filter(p => p.active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(p => ({ id: p.id, name: p.name || p.id }));
};

// Select of plan names when the catalog has plans for the product; falls back
// to a free-text plan-id input when it doesn't (e.g. catalog not seeded yet).
const PlanPicker: React.FC<{
  id: string;
  ariaLabel: string;
  options: PlanOption[];
  value: string;
  onChange: (planId: string) => void;
}> = ({ id, ariaLabel, options, value, onChange }) => {
  if (options.length === 0) {
    return (
      <input
        id={id}
        aria-label={ariaLabel}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="starter"
        className="w-full px-3 py-2 rounded-lg glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
      />
    );
  }
  return (
    <select
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
    >
      {options.every(o => o.id !== value) && <option value={value}>{value}</option>}
      {options.map(o => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );
};

// Session cache so the page renders instantly on revisit while a fresh
// list loads in the background (same pattern as the plan catalog cache).
const USERS_CACHE_KEY = 'bioaxis:admin-users:v1';

const readCachedUsers = (): UserData[] | null => {
  try {
    const raw = sessionStorage.getItem(USERS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed.users) ? parsed.users : null;
  } catch {
    return null;
  }
};

const writeCachedUsers = (users: UserData[]) => {
  try {
    sessionStorage.setItem(USERS_CACHE_KEY, JSON.stringify({ at: Date.now(), users }));
  } catch {}
};

const statusBadge = (status: string) => {
  const colors: Record<string, string> = {
    active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    trialing: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    past_due: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    canceled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    none: 'bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-400',
  };
  return colors[status] || colors.none;
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedUid, setExpandedUid] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [grantProductId, setGrantProductId] = useState<ProductId>('vetaxis');
  const [grantPlanId, setGrantPlanId] = useState(DEFAULT_PLAN_BY_PRODUCT.vetaxis);
  const [grantStatus, setGrantStatus] = useState<EntitlementStatus>('active');
  const [grantTrialDays, setGrantTrialDays] = useState(30);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteProductId, setInviteProductId] = useState<ProductId>('vetaxis');
  const [invitePlanId, setInvitePlanId] = useState(DEFAULT_PLAN_BY_PRODUCT.vetaxis);
  const [inviteTrialDays, setInviteTrialDays] = useState(30);
  const [inviteSending, setInviteSending] = useState(false);
  const [planCatalog, setPlanCatalog] = useState<PlanCatalog | null>(null);

  useEffect(() => {
    let mounted = true;
    const cached = readCachedPlanCatalog();
    if (cached) setPlanCatalog(cached);
    callGetPlanCatalog()
      .then((data) => { if (mounted) setPlanCatalog(data); })
      .catch(() => {}); // fall back to free-text plan input
    return () => { mounted = false; };
  }, []);

  const defaultPlanFor = (catalog: PlanCatalog | null, productId: ProductId) =>
    planOptionsFor(catalog, productId)[0]?.id || DEFAULT_PLAN_BY_PRODUCT[productId];

  const loadUsers = async () => {
    // Render the cached list immediately; only show the spinner on a cold visit.
    const cached = readCachedUsers();
    if (cached && cached.length) {
      setUsers(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const { users: list } = await callAdminListUsers({ limit: 200 });
      setUsers(list);
      writeCachedUsers(list);
    } catch (err: any) {
      if (!cached || !cached.length) {
        setError(err?.message || 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const toggleRole = async (uid: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    setActionLoading(`role-${uid}`);
    try {
      await callAdminSetUserRole({ targetUid: uid, role: newRole as 'user' | 'admin' });
      setUsers(prev => prev.map(u => u.uid === uid ? { ...u, role: newRole } : u));
      setMessage(`Role updated to ${newRole}`);
    } catch (err: any) {
      setMessage(err?.message || 'Failed to update role');
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const manageSubscription = async (uid: string, productId: string, action: 'cancel' | 'pause' | 'unpause' | 'fetch') => {
    setActionLoading(`sub-${uid}-${productId}-${action}`);
    try {
      await callAdminManageSubscription({ targetUid: uid, productId, action });
      setMessage(`Subscription ${action} successful`);
      await loadUsers();
    } catch (err: any) {
      setMessage(err?.message || `Failed to ${action} subscription`);
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const grantEntitlement = async (uid: string) => {
    const cleanPlanId = grantPlanId.trim();
    if (!cleanPlanId) {
      setMessage('Please enter a plan ID');
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    setActionLoading(`grant-${uid}`);
    try {
      await callAdminGrantEntitlement({
        targetUid: uid,
        productId: grantProductId,
        planId: cleanPlanId,
        status: grantStatus,
        ...(grantStatus === 'trialing' ? { trialDays: grantTrialDays } : {}),
      });
      setMessage(grantStatus === 'trialing'
        ? `Trial granted: ${grantProductId} (${grantTrialDays} days)`
        : `Entitlement added: ${grantProductId} (${grantStatus})`);
      await loadUsers();
    } catch (err: any) {
      setMessage(err?.message || 'Failed to add entitlement');
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const inviteClient = async () => {
    const email = inviteEmail.trim();
    if (!email || !email.includes('@')) {
      setMessage('Please enter a valid client email');
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    setInviteSending(true);
    try {
      const res = await callAdminInviteClient({
        email,
        productId: inviteProductId,
        planId: invitePlanId.trim(),
        trialDays: inviteTrialDays,
      });
      setMessage(res.emailSent
        ? `Invite sent to ${email} — ${inviteTrialDays}-day ${inviteProductId} trial${res.isNewUser ? ' (new account created)' : ''}`
        : `Trial granted for ${email}, but the invite email failed to send — share the login link manually.`);
      setInviteEmail('');
      setShowInvite(false);
      await loadUsers();
    } catch (err: any) {
      setMessage(err?.message || 'Failed to send invite');
    } finally {
      setInviteSending(false);
      setTimeout(() => setMessage(null), 6000);
    }
  };

  const deleteUser = async (uid: string, email: string) => {
    const confirmed = window.confirm(
      `Delete ${email}?\n\nThis permanently removes their BioAxis login, profile, and entitlements. This cannot be undone.`
    );
    if (!confirmed) return;
    setActionLoading(`delete-${uid}`);
    try {
      await callAdminDeleteUser({ targetUid: uid });
      setUsers(prev => {
        const next = prev.filter(u => u.uid !== uid);
        writeCachedUsers(next);
        return next;
      });
      setExpandedUid(null);
      setMessage(`Deleted ${email}`);
    } catch (err: any) {
      setMessage(err?.message || 'Failed to delete user');
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.email || '').toLowerCase().includes(q) ||
      (u.displayName || '').toLowerCase().includes(q) ||
      u.uid.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <motion.div className="glass-effect rounded-2xl p-8 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-600 dark:text-gray-300">Loading users...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">User Management</h1>
              <p className="text-slate-600 dark:text-gray-400">{users.length} users registered</p>
            </div>
          </div>
        </motion.div>

        {/* Messages */}
        <AnimatePresence>
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-6 p-3 rounded-xl glass-effect border border-primary-200 dark:border-primary-800/30 text-sm text-primary-700 dark:text-primary-300"
            >
              {message}
            </motion.div>
          )}
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex items-center gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search + Invite */}
        <motion.div className="mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-md flex-1 min-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by email, name, or UID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowInvite(v => !v)}
              className="btn-primary flex items-center gap-2 py-3"
            >
              <UserPlus className="w-5 h-5" />
              Invite Client
            </button>
          </div>

          <AnimatePresence>
            {showInvite && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4 p-5 rounded-2xl glass-effect border border-slate-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Invite a client with a free trial
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-4">
                    Creates their account, activates the trial, and emails them a set-up link. Existing accounts just get the trial and a sign-in link.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label htmlFor="invite-email" className="block text-xs text-slate-500 dark:text-gray-400 mb-1">Client email</label>
                      <input
                        id="invite-email"
                        aria-label="Invite client email"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="client@practice.co.za"
                        className="w-full px-3 py-2 rounded-lg glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="invite-product" className="block text-xs text-slate-500 dark:text-gray-400 mb-1">Product</label>
                      <select
                        id="invite-product"
                        aria-label="Invite product"
                        value={inviteProductId}
                        onChange={(e) => {
                          const next = e.target.value as ProductId;
                          setInviteProductId(next);
                          setInvitePlanId(defaultPlanFor(planCatalog, next));
                        }}
                        className="w-full px-3 py-2 rounded-lg glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      >
                        {PRODUCT_OPTIONS.map((product) => (
                          <option key={product.id} value={product.id}>{product.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="invite-plan" className="block text-xs text-slate-500 dark:text-gray-400 mb-1">Plan</label>
                      <PlanPicker
                        id="invite-plan"
                        ariaLabel="Invite plan"
                        options={planOptionsFor(planCatalog, inviteProductId)}
                        value={invitePlanId}
                        onChange={setInvitePlanId}
                      />
                    </div>
                    <div>
                      <label htmlFor="invite-days" className="block text-xs text-slate-500 dark:text-gray-400 mb-1">Trial length (days)</label>
                      <input
                        id="invite-days"
                        aria-label="Invite trial days"
                        type="number"
                        min={1}
                        max={365}
                        value={inviteTrialDays}
                        onChange={(e) => setInviteTrialDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
                        className="w-full px-3 py-2 rounded-lg glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <button
                      onClick={inviteClient}
                      disabled={inviteSending || !inviteEmail.trim()}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50"
                    >
                      {inviteSending ? 'Sending invite...' : 'Send Invite'}
                    </button>
                    <button
                      onClick={() => setShowInvite(false)}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* User List */}
        <motion.div className="space-y-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          {filtered.map((u) => {
            const isExpanded = expandedUid === u.uid;
            return (
              <motion.div
                key={u.uid}
                layout
                className="glass-effect rounded-2xl shadow-glass overflow-hidden"
              >
                {/* User row */}
                <button
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50/50 dark:hover:bg-gray-800/30 transition-colors"
                  onClick={() => setExpandedUid(isExpanded ? null : u.uid)}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary-500/10 dark:bg-primary-400/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                        {(u.email || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                          {u.email}
                        </span>
                        {u.role === 'admin' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Admin
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-500 dark:text-gray-400">
                        {u.displayName || 'No name'} &middot; {u.uid.substring(0, 12)}...
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {u.entitlements && Object.entries(u.entitlements).map(([pid, ent]) => (
                      ent.status !== 'none' && (
                        <span key={pid} className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(ent.status)} capitalize`}>
                          {pid}
                        </span>
                      )
                    ))}
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </button>

                {/* Expanded details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-200 dark:border-gray-700 overflow-hidden"
                    >
                      <div className="p-6 space-y-4">
                        {/* Role management */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600 dark:text-gray-400">Role</span>
                          <button
                            onClick={() => toggleRole(u.uid, u.role)}
                            disabled={actionLoading === `role-${u.uid}`}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              u.role === 'admin'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200'
                                : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 hover:bg-primary-200'
                            } disabled:opacity-50`}
                          >
                            {actionLoading === `role-${u.uid}` ? 'Updating...' : u.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                          </button>
                        </div>

                        {/* Entitlements */}
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-3">Subscriptions</h4>
                          <div className="space-y-3">
                            {u.entitlements && Object.entries(u.entitlements).map(([pid, ent]) => (
                              <div key={pid} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-gray-800/50">
                                <div>
                                  <span className="text-sm font-medium text-slate-900 dark:text-white capitalize">{pid}</span>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(ent.status)}`}>
                                      {ent.status}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-gray-400 capitalize">{ent.plan} plan</span>
                                    {ent.status === 'trialing' && trialEndsLabel(ent.trialEndsAt) && (
                                      <span className="text-xs text-blue-600 dark:text-blue-400">
                                        Trial ends {trialEndsLabel(ent.trialEndsAt)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {ent.payfast?.token && (
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => manageSubscription(u.uid, pid, 'fetch')}
                                      disabled={!!actionLoading}
                                      className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                                      title="Refresh subscription"
                                    >
                                      <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${actionLoading === `sub-${u.uid}-${pid}-fetch` ? 'animate-spin' : ''}`} />
                                    </button>
                                    {ent.status === 'active' && (
                                      <>
                                        <button
                                          onClick={() => manageSubscription(u.uid, pid, 'pause')}
                                          disabled={!!actionLoading}
                                          className="p-1.5 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/20 transition-colors disabled:opacity-50"
                                          title="Pause subscription"
                                        >
                                          <Pause className="w-3.5 h-3.5 text-yellow-600" />
                                        </button>
                                        <button
                                          onClick={() => manageSubscription(u.uid, pid, 'cancel')}
                                          disabled={!!actionLoading}
                                          className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                                          title="Cancel subscription"
                                        >
                                          <XCircle className="w-3.5 h-3.5 text-red-600" />
                                        </button>
                                      </>
                                    )}
                                    {ent.payfast?.subscriptionStatus === 'PAUSED' && (
                                      <button
                                        onClick={() => manageSubscription(u.uid, pid, 'unpause')}
                                        disabled={!!actionLoading}
                                        className="p-1.5 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50"
                                        title="Unpause subscription"
                                      >
                                        <Play className="w-3.5 h-3.5 text-green-600" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Manual entitlement */}
                        <div className="pt-2 border-t border-slate-200 dark:border-gray-700">
                          <h4 className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-3">Manual Entitlement</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div>
                              <label htmlFor={`manual-product-${u.uid}`} className="block text-xs text-slate-500 dark:text-gray-400 mb-1">
                                Product
                              </label>
                              <select
                                id={`manual-product-${u.uid}`}
                                aria-label="Entitlement product"
                                value={grantProductId}
                                onChange={(e) => {
                                  const nextProduct = e.target.value as ProductId;
                                  setGrantProductId(nextProduct);
                                  setGrantPlanId(defaultPlanFor(planCatalog, nextProduct));
                                }}
                                className="w-full px-3 py-2 rounded-lg glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                              >
                                {PRODUCT_OPTIONS.map((product) => (
                                  <option key={product.id} value={product.id}>{product.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label htmlFor={`manual-plan-${u.uid}`} className="block text-xs text-slate-500 dark:text-gray-400 mb-1">
                                Plan
                              </label>
                              <PlanPicker
                                id={`manual-plan-${u.uid}`}
                                ariaLabel="Entitlement plan"
                                options={planOptionsFor(planCatalog, grantProductId)}
                                value={grantPlanId}
                                onChange={setGrantPlanId}
                              />
                            </div>
                            <div>
                              <label htmlFor={`manual-status-${u.uid}`} className="block text-xs text-slate-500 dark:text-gray-400 mb-1">
                                Status
                              </label>
                              <select
                                id={`manual-status-${u.uid}`}
                                aria-label="Entitlement status"
                                value={grantStatus}
                                onChange={(e) => setGrantStatus(e.target.value as EntitlementStatus)}
                                className="w-full px-3 py-2 rounded-lg glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                              >
                                <option value="active">active</option>
                                <option value="trialing">trialing</option>
                                <option value="past_due">past_due</option>
                                <option value="canceled">canceled</option>
                                <option value="none">none</option>
                              </select>
                            </div>
                            {grantStatus === 'trialing' && (
                              <div>
                                <label htmlFor={`manual-trial-days-${u.uid}`} className="block text-xs text-slate-500 dark:text-gray-400 mb-1">
                                  Trial length (days)
                                </label>
                                <input
                                  id={`manual-trial-days-${u.uid}`}
                                  aria-label="Trial length in days"
                                  type="number"
                                  min={1}
                                  max={365}
                                  value={grantTrialDays}
                                  onChange={(e) => setGrantTrialDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
                                  className="w-full px-3 py-2 rounded-lg glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                />
                              </div>
                            )}
                          </div>
                          <div className="mt-3">
                            <button
                              onClick={() => grantEntitlement(u.uid)}
                              disabled={!!actionLoading || !grantPlanId.trim()}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/40 transition-colors disabled:opacity-50"
                            >
                              {actionLoading === `grant-${u.uid}` ? 'Adding...' : 'Add Entitlement'}
                            </button>
                          </div>
                        </div>

                        {/* Quick actions */}
                        <div className="flex items-center justify-between gap-2 pt-2">
                          <Link
                            to={`/admin/payment-links?uid=${u.uid}&email=${encodeURIComponent(u.email)}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 hover:bg-primary-200 transition-colors"
                          >
                            <LinkIcon className="w-3.5 h-3.5" /> Generate Payment Link
                          </Link>
                          {u.role !== 'admin' && (
                            <button
                              onClick={() => deleteUser(u.uid, u.email)}
                              disabled={!!actionLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {actionLoading === `delete-${u.uid}` ? 'Deleting...' : 'Delete User'}
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500 dark:text-gray-400">
              {search ? 'No users match your search.' : 'No users found.'}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminUsers;
